import { Control, Controller } from "react-hook-form"
import { Trans, t } from "@lingui/macro"
import { useCallback, useEffect, useMemo, useState } from "react"

import { IdentityType } from "../../../../../../api/stores/checkout"
import { TextField } from "../../../../../../components/TextField"
import { i18n } from "@lingui/core"

interface IdentityNumberFieldProps {
  name: string
  errorMsg?: string
  control: Control<any>
  selectedIdentityType: IdentityType
  disabled?: boolean
  defaultValue?: string
}

type LocaleConfig = {
  [key: string]: {
    maxLength: number
    inputRegEx: RegExp
    mask?: {
      [key: string]: (value: string) => string
    }
    formatValidation?: {
      [key: string]: {
        regEx: RegExp
        message: string
      }
    }
    numberValidation?: {
      [key: string]: {
        validator: (value: string) => boolean | string
      }
    }
  }
}

export function IdentityNumberController({
  name = "contactIdentityNumber",
  errorMsg,
  control,
  selectedIdentityType = "DU",
  disabled = false,
  defaultValue = "",
}: IdentityNumberFieldProps) {
  const [_value, _setValue] = useState<string>("")

  const LOCALE_CONFIG: LocaleConfig = useMemo(
    () => ({
      "es-AR": {
        maxLength: 11,
        inputRegEx: /[^\d]/gi,
      },
      "es-MX": {
        maxLength: 18,
        inputRegEx: /[^a-zA-Z0-9]/gi,
      },
      "pt-BR": {
        maxLength: selectedIdentityType === "DU" ? 11 : 14,
        inputRegEx: /[^\d]/gi,
        mask: {
          DU: (value: string) => {
            const truncatedValue = value.slice(0, 11)

            if (!truncatedValue) return ""

            if (truncatedValue.length > 9) {
              return `${formatWithThousandsSeparator(
                truncatedValue.slice(0, 9)
              )}-${truncatedValue.slice(
                9,
                Math.min(truncatedValue.length, 11)
              )}`
            }

            return formatWithThousandsSeparator(truncatedValue)
          },
          CUIT: (value: string) => {
            const truncatedValue = value.slice(0, 14)

            if (!truncatedValue) return ""

            if (truncatedValue.length > 8 && truncatedValue.length <= 12) {
              return `${formatWithThousandsSeparator(
                truncatedValue.slice(0, 8)
              )}/${truncatedValue.slice(
                8,
                Math.min(truncatedValue.length, 12)
              )}`
            }

            if (truncatedValue.length > 12) {
              return `${formatWithThousandsSeparator(
                truncatedValue.slice(0, 8)
              )}/${truncatedValue.slice(
                8,
                Math.min(truncatedValue.length, 12)
              )}-${truncatedValue.slice(
                12,
                Math.max(truncatedValue.length, 14)
              )}`
            }

            return String(formatWithThousandsSeparator(value))
          },
        },
        formatValidation: {
          DU: {
            regEx: /^\d{11}$/,
            message: t`El CPF ingresado no es válido.`,
          },
          CUIT: {
            regEx: /^\d{8}0001\d{2}$/,
            message: t`Usá el formato XX.XXX.XXX/0001-XX o XXXXXXXX/0001-XX`,
          },
        },
        numberValidation: {
          DU: {
            validator: (value: string) => {
              const cpf = value

              if (cpf.length !== 11) return t`El CPF debe tener 11 dígitos.`

              if (/^(\d)\1{10}$/.test(cpf))
                return t`El CPF no puede tener todos los dígitos iguales.`

              const base9 = cpf.slice(0, 9)
              const d1 = calculateVerifierDigit(base9, 10)
              const base10 = base9 + String(d1)
              const d2 = calculateVerifierDigit(base10, 11)

              return (
                cpf === base10 + String(d2) || t`El CPF ingresado no es válido.`
              )
            },
          },
        },
      },
    }),
    [selectedIdentityType]
  )

  const parseField = useCallback(
    (value: string) => {
      return value
        .replace(LOCALE_CONFIG[i18n.locale].inputRegEx, "")
        .slice(0, LOCALE_CONFIG[i18n.locale].maxLength)
    },
    [LOCALE_CONFIG]
  )

  const mask = useCallback(
    (value: string) => {
      const maskFunction = LOCALE_CONFIG[i18n.locale].mask

      if (!maskFunction || !maskFunction[selectedIdentityType]) return value

      return maskFunction[selectedIdentityType](value)
    },
    [LOCALE_CONFIG, selectedIdentityType]
  )

  useEffect(() => {
    if (defaultValue) {
      _setValue(mask(parseField(defaultValue)))
    }
  }, [defaultValue, mask, parseField])

  return (
    <Controller
      control={control}
      name={name}
      defaultValue=""
      rules={{
        required: t`Completá el campo con tu documento.`,
        validate: {
          checkCorrectFormat: checkCorrectFormat,
          checkCorrectNumber: checkCorrectNumber,
        },
      }}
      render={({ onChange }) => (
        <TextField
          name={name}
          label={<Trans>Número de documento</Trans>}
          type="text"
          errorMsg={errorMsg}
          hideLength={true}
          onChange={e => {
            onChange(parseField(e.target.value))
            _setValue(mask(parseField(e.target.value)))
          }}
          value={_value}
          disabled={disabled}
        />
      )}
    />
  )

  function checkCorrectFormat(value: string): string | boolean {
    if (!value) return true

    const formatValidation = LOCALE_CONFIG[i18n.locale].formatValidation

    if (!formatValidation || !formatValidation[selectedIdentityType])
      return true

    const { regEx, message } = formatValidation[selectedIdentityType]

    return regEx.test(value) || message
  }

  function checkCorrectNumber(value: string): string | boolean {
    if (!value) return true

    const numberValidation = LOCALE_CONFIG[i18n.locale].numberValidation

    if (!numberValidation || !numberValidation[selectedIdentityType])
      return true

    const { validator } = numberValidation[selectedIdentityType]

    return validator(value)
  }

  function calculateVerifierDigit(base: string, factorStart: number): number {
    let sum = 0

    for (let i = 0; i < base.length; i++) {
      sum += parseInt(base[i], 10) * (factorStart - i)
    }

    const mod = sum % 11

    return mod < 2 ? 0 : 11 - mod
  }

  function formatWithThousandsSeparator(value: string): string {
    if (!value) return ""

    const reversed = value.split("").reverse().join("")

    const formatted = reversed.replace(/(\d{3})(?=\d)/g, "$1.")

    return formatted.split("").reverse().join("")
  }
}
